import { Switch } from "@/components/Switch";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { IconSettings } from "@/assets/icons/geist/IconSettings";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { ConfigureCrowdStrikeModal } from "@/routes/settings/organization/policies/ConfigureCrowdStrikeModal";

type SecurityAgentsProps = {
    editable?: boolean;
};

export const SecurityAgents: FC<SecurityAgentsProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [enabled, setEnabled] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    useEffect(() => {
        if (organizationPolicies?.securityAgentPolicy?.crowdstrike) {
            setEnabled(!!organizationPolicies.securityAgentPolicy.crowdstrike.enabled);
        }
    }, [organizationPolicies]);

    const handleToggle = useCallback(
        async (newValue: boolean) => {
            const params: PartialMessage<OrganizationPolicies> = {
                securityAgentPolicy: {
                    crowdstrike: {
                        enabled: newValue,
                    },
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                setEnabled(newValue);
                toast({
                    title: newValue ? "CrowdStrike Falcon enabled" : "CrowdStrike Falcon disabled",
                });
            } catch (error) {
                console.error(error);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [updateOrganizationPolicies, toast],
    );

    const handleOpenModal = useCallback(() => {
        setShowModal(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, []);

    return (
        <SkeletonBlock ready={!isLoading}>
            <div className="rounded-xl border border-border-base">
                <div className="flex flex-col justify-between gap-4 px-5 py-4 lg:flex-row lg:items-center lg:gap-8">
                    <div className="flex min-w-0 items-center gap-4">
                        <Switch
                            id="crowdstrike-falcon-switch"
                            state={enabled ? "checked" : "unchecked"}
                            onToggle={handleToggle}
                            disabled={!editable}
                            aria-label="Enable CrowdStrike Falcon"
                            data-tracking-id="toggle-crowdstrike-falcon"
                        />
                        <div className="flex min-w-0 flex-col gap-1">
                            <Text className="text-base font-semibold">CrowdStrike Falcon</Text>
                            <Text className="text-sm text-content-secondary">
                                Deploy CrowdStrike Falcon sensor to all environments.{" "}
                                <ExternalLink
                                    href="https://ona.com/docs/ona/organizations/policies#security-agents"
                                    iconSize="sm"
                                >
                                    Learn more
                                </ExternalLink>
                            </Text>
                        </div>
                    </div>
                    <Button
                        variant="secondary"
                        onClick={handleOpenModal}
                        disabled={!editable}
                        data-testid="configure-crowdstrike-button"
                        className="flex items-center gap-2 self-start lg:self-auto"
                        data-tracking-id="configure-crowdstrike-settings"
                    >
                        <IconSettings size="base" />
                        Settings
                    </Button>
                </div>
            </div>

            {showModal && (
                <ConfigureCrowdStrikeModal
                    currentConfig={organizationPolicies?.securityAgentPolicy?.crowdstrike}
                    onClose={handleCloseModal}
                />
            )}
        </SkeletonBlock>
    );
};
